export const FEEDBACK_MAX_TEXT_LENGTH = 3_000;
export const FEEDBACK_MAX_FILES = 4;
export const FEEDBACK_MAX_PHOTO_BYTES = 10 * 1_024 * 1_024;
export const FEEDBACK_MAX_VIDEO_BYTES = 20 * 1_024 * 1_024;
export const FEEDBACK_MAX_TOTAL_FILE_BYTES = 30 * 1_024 * 1_024;

export type FeedbackKind = 'bug' | 'idea' | 'other';
export type FeedbackMark = 'bold' | 'italic';

export interface FeedbackRichTextSpan {
  readonly text: string;
  readonly marks?: readonly FeedbackMark[];
}

export interface FeedbackRichTextBlock {
  readonly type: 'paragraph' | 'bullet';
  readonly spans: readonly FeedbackRichTextSpan[];
}

export interface FeedbackRichTextDocument {
  readonly blocks: readonly FeedbackRichTextBlock[];
}

const KINDS = new Set<FeedbackKind>(['bug', 'idea', 'other']);
const MARKS = new Set<FeedbackMark>(['bold', 'italic']);

function record(value: unknown): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new TypeError('Ожидался объект rich text');
  }
  return value as Record<string, unknown>;
}

function exactKeys(value: Record<string, unknown>, allowed: readonly string[]): void {
  if (Object.keys(value).some((key) => !allowed.includes(key))) {
    throw new TypeError('Rich text содержит неизвестное поле');
  }
}

export function parseFeedbackKind(value: unknown): FeedbackKind {
  if (typeof value !== 'string' || !KINDS.has(value as FeedbackKind)) {
    throw new TypeError('Некорректный тип фидбека');
  }
  return value as FeedbackKind;
}

export function feedbackDocumentText(document: FeedbackRichTextDocument): string {
  return document.blocks
    .map((block) => `${block.type === 'bullet' ? '• ' : ''}${block.spans.map(({ text }) => text).join('')}`)
    .join('\n')
    .trim();
}

export function parseFeedbackRichText(value: unknown): FeedbackRichTextDocument {
  const root = record(value);
  exactKeys(root, ['blocks']);
  if (!Array.isArray(root.blocks) || root.blocks.length > 100) {
    throw new TypeError('Некорректные блоки rich text');
  }
  let spanCount = 0;
  const blocks: FeedbackRichTextBlock[] = root.blocks.map((rawBlock) => {
    const block = record(rawBlock);
    exactKeys(block, ['type', 'spans']);
    if ((block.type !== 'paragraph' && block.type !== 'bullet') || !Array.isArray(block.spans)) {
      throw new TypeError('Некорректный блок rich text');
    }
    if (block.spans.length > 100 || (spanCount += block.spans.length) > 300) {
      throw new TypeError('Слишком сложный rich text');
    }
    const spans: FeedbackRichTextSpan[] = block.spans.map((rawSpan) => {
      const span = record(rawSpan);
      exactKeys(span, ['text', 'marks']);
      if (typeof span.text !== 'string' || span.text.includes('\u0000')) {
        throw new TypeError('Некорректный текст');
      }
      if (span.marks !== undefined && (!Array.isArray(span.marks)
        || span.marks.length > 2
        || new Set(span.marks).size !== span.marks.length
        || span.marks.some((mark) => typeof mark !== 'string' || !MARKS.has(mark as FeedbackMark)))) {
        throw new TypeError('Некорректное форматирование');
      }
      return {
        text: span.text,
        ...(span.marks === undefined || span.marks.length === 0
          ? {}
          : { marks: span.marks as FeedbackMark[] }),
      };
    });
    return { type: block.type, spans };
  });
  const document = { blocks } satisfies FeedbackRichTextDocument;
  const text = feedbackDocumentText(document);
  if (text.length === 0 || [...text].length > FEEDBACK_MAX_TEXT_LENGTH) {
    throw new RangeError(`Описание должно содержать от 1 до ${FEEDBACK_MAX_TEXT_LENGTH} символов`);
  }
  return document;
}

export function escapeTelegramHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

export function feedbackDocumentTelegramHtml(document: FeedbackRichTextDocument): string {
  return document.blocks.map((block) => {
    const body = block.spans.map((span) => {
      let text = escapeTelegramHtml(span.text);
      if (span.marks?.includes('italic')) text = `<i>${text}</i>`;
      if (span.marks?.includes('bold')) text = `<b>${text}</b>`;
      return text;
    }).join('');
    return `${block.type === 'bullet' ? '• ' : ''}${body}`;
  }).join('\n');
}
